import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};
const stagger = {
  visible: { transition: { staggerChildren: 0.15 } },
};

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((res) => setPosts(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page" className="pt-20">
      {/* Header */}
      <section className="py-24 md:py-32 bg-[#E5E0D8]/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeIn} className="text-sm tracking-[0.3em] uppercase text-[#C5A065] font-medium">
              Blog
            </motion.p>
            <motion.h1
              variants={fadeIn}
              className="font-heading text-5xl md:text-7xl font-medium tracking-tight text-[#2C2C2C] mt-4"
              data-testid="blog-h1"
            >
              Actualités &<br />Conseils experts
            </motion.h1>
            <motion.div variants={fadeIn} className="section-divider mt-8 mb-8" />
            <motion.p variants={fadeIn} className="text-lg font-light leading-relaxed text-[#2C2C2C]/70 max-w-2xl">
              Retrouvez nos articles sur la rénovation, les normes en vigueur 
              et nos conseils pour réussir vos projets de transformation intérieure.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center text-[#8A8A8A]">Chargement...</div>
          ) : (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {posts.map((post, i) => (
                <motion.div key={post.id} variants={fadeIn}>
                  <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${i}`}>
                    <Card className="bg-white border border-border/50 rounded-none shadow-none hover:border-[#C5A065]/50 hover:shadow-md transition-all duration-300 group overflow-hidden h-full">
                      <div className="img-zoom">
                        <img
                          src={post.image_url || "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80"}
                          alt={post.title}
                          className="blog-card-img w-full"
                          loading="lazy"
                        />
                      </div>
                      <CardContent className="p-8">
                        <div className="flex items-center gap-3 mb-4">
                          <Badge
                            variant="secondary"
                            className="rounded-none text-xs tracking-wider uppercase bg-[#E5E0D8] text-[#2C2C2C]/70 hover:bg-[#E5E0D8]"
                          >
                            {post.category}
                          </Badge>
                          <span className="flex items-center gap-1 text-xs text-[#8A8A8A]">
                            <Calendar className="w-3 h-3" />
                            {formatDate(post.published_at)}
                          </span>
                        </div>
                        <h3 className="font-heading text-xl font-medium text-[#2C2C2C] group-hover:text-[#C5A065] transition-colors duration-300 mb-3">
                          {post.title}
                        </h3>
                        <p className="text-sm leading-relaxed text-[#8A8A8A] mb-4">
                          {post.excerpt}
                        </p>
                        <span className="inline-flex items-center text-sm text-[#C5A065] font-medium tracking-wide">
                          Lire la suite
                          <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}
